"use client";

import Image from "next/image";
import { motion } from "framer-motion";
import { Clock3, ExternalLink } from "lucide-react";
import { Drawer } from "vaul";
import { projects } from "@/features/home/data";
import { useHaptic } from "@/hooks/use-haptic";
import {
  staggerContainer,
  staggerItem,
  fadeUp,
  sectionLabel,
} from "@/lib/motion";

const VP = { once: true, amount: 0.05 };

function ProjectCard({ project }: { project: (typeof projects)[0] }) {
  const haptic = useHaptic();

  const cta = !project.isReleased ? (
    <span className="inline-flex h-8 items-center gap-1.5 rounded-full border border-amber-400/25 bg-amber-400/10 px-3 text-xs font-semibold text-amber-700">
      <Clock3 className="w-3.5 h-3.5" aria-hidden="true" />
      <span>Yakında</span>
    </span>
  ) : (
    <div className="flex items-center gap-1 text-primary font-medium text-xs sm:text-sm">
      <span>Projeyi İncele</span>
      <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4" aria-hidden="true" />
    </div>
  );

  const details = (
    <>
      <div>
        <span className="text-xs text-primary font-medium uppercase tracking-wider">
          {project.category}
        </span>
        <h3 className="font-[family-name:var(--font-heading)] text-base sm:text-xl font-bold text-foreground mt-1.5 mb-2">
          {project.title}
        </h3>
        <p className="text-muted-foreground text-xs sm:text-sm leading-relaxed line-clamp-3">
          {project.description}
        </p>
      </div>

      <div>
        <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3">
          {project.tech.map((tech) => (
            <span
              key={tech}
              className="px-2 py-0.5 text-[10px] sm:text-xs font-[family-name:var(--font-mono)] bg-secondary rounded text-muted-foreground"
            >
              {tech}
            </span>
          ))}
        </div>
        {cta}
      </div>
    </>
  );

  return (
    <Drawer.Root>
      <Drawer.Trigger asChild onClick={() => haptic("light")}>
        <motion.div variants={staggerItem} className="group relative transition-transform active:scale-[0.98] min-w-[85vw] snap-center sm:min-w-0 cursor-pointer text-left">
      <div className="relative overflow-hidden rounded-3xl glass hairline">
        <div className="relative aspect-[4/3] overflow-hidden">
          {project.image ? (
            <Image
              src={project.image}
              alt={`${project.title} proje görseli`}
              fill
              sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
              className="object-cover transition-transform duration-500 md:group-hover:scale-105"
            />
          ) : null}

          <div
            className={`absolute inset-0 bg-gradient-to-br ${project.color} ${
              project.image ? "opacity-35" : ""
            }`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background/35 via-transparent to-transparent" />

          {!project.isReleased && (
            <div className="absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-full border border-amber-300/30 bg-background/95 px-3 py-1.5 text-xs font-semibold text-amber-700 shadow-sm">
              <Clock3 className="w-3.5 h-3.5" aria-hidden="true" />
              <span>Yakında</span>
            </div>
          )}

          <div className="absolute inset-0 hidden flex-col justify-between bg-background/92 p-5 opacity-0 transition-opacity duration-300 md:flex md:group-hover:opacity-100">
            {details}
          </div>
        </div>

        <div className="flex min-h-[190px] flex-col justify-between p-4 sm:p-5 md:hidden">
          {details}
        </div>
      </div>
      </motion.div>
      </Drawer.Trigger>
      
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50" />
        <Drawer.Content className="bg-background flex flex-col rounded-t-[2rem] h-[85vh] sm:h-auto sm:max-h-[85vh] fixed bottom-0 left-0 right-0 z-50 border border-border/50 shadow-2xl focus:outline-none">
           <div className="p-6 flex-1 overflow-y-auto hide-scrollbar flex flex-col">
              <div className="mx-auto w-12 h-1.5 rounded-full bg-border mb-8 shrink-0" />
              
              <div className="relative aspect-video rounded-2xl overflow-hidden mb-6 shrink-0 bg-secondary/50 flex items-center justify-center">
                 {project.image ? (
                   <Image src={project.image} fill className="object-cover" alt={project.title} />
                 ) : (
                   <div className="text-muted-foreground">Görsel Yok</div>
                 )}
                 <div className={`absolute inset-0 bg-gradient-to-br ${project.color} opacity-20`} />
              </div>

              <span className="text-sm text-primary font-bold uppercase tracking-wider mb-2">
                {project.category}
              </span>
              <Drawer.Title className="font-[family-name:var(--font-heading)] text-3xl font-bold mb-4">{project.title}</Drawer.Title>
              <p className="text-muted-foreground leading-relaxed mb-8 text-lg">{project.description}</p>
              
              <h4 className="font-semibold mb-4 text-foreground">Kullanılan Teknolojiler</h4>
              <div className="flex flex-wrap gap-2 mb-8">
                 {project.tech.map((tech) => (
                   <span key={tech} className="px-4 py-2 bg-secondary rounded-xl text-sm font-medium text-foreground">
                     {tech}
                   </span>
                 ))}
              </div>

              <div className="mt-auto pt-6 border-t border-border/50 flex flex-col gap-3">
                {project.isReleased && project.link && (
                  <a 
                    href={project.link} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="w-full bg-primary text-primary-foreground py-4 rounded-xl font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                    onClick={() => haptic("medium")}
                  >
                    Siteyi Ziyaret Et <ExternalLink className="w-5 h-5" />
                  </a>
                )}
                <Drawer.Close asChild>
                  <button onClick={() => haptic("light")} className="w-full bg-secondary text-secondary-foreground py-4 rounded-xl font-semibold active:scale-[0.98] transition-transform">
                    Kapat
                  </button>
                </Drawer.Close>
              </div>
           </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

export function PortfolioSection() {
  return (
    <section
      id="projeler"
      className="relative py-16 sm:py-24 lg:py-32"
      aria-label="Portföy projelerimiz"
    >
      <div className="brand-divider" />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 max-w-6xl">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={VP}
          className="text-center max-w-3xl mx-auto mb-12 sm:mb-16"
        >
          <motion.span
            variants={sectionLabel}
            className="text-primary font-medium text-sm uppercase tracking-wider inline-block"
          >
            Projelerimiz
          </motion.span>
          <motion.h2
            variants={fadeUp}
            className="font-[family-name:var(--font-heading)] text-3xl sm:text-4xl md:text-5xl font-bold mt-4 mb-4 sm:mb-6 text-foreground leading-tight"
          >
            Başarı <span className="text-gradient-brand">Hikayelerimiz</span>
          </motion.h2>
          <motion.p
            variants={fadeUp}
            className="text-muted-foreground text-base sm:text-lg leading-snug sm:leading-relaxed"
          >
            Farklı sektörlerden müşterilerimiz için geliştirdiğimiz projelerden
            bazılarını inceleyin.
          </motion.p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={VP}
          className="flex overflow-x-auto snap-x snap-mandatory hide-scrollbar gap-4 pb-8 -mx-4 px-4 sm:mx-0 sm:px-0 sm:pb-0 sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-6"
        >
          {projects.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </motion.div>
      </div>
    </section>
  );
}
